/**
 * Excel DATA VALIDATION (list) → Univer in-cell dropdowns.
 *
 * WHY THIS EXISTS (client 2026-08-20, "some columns have a dropdown with data in
 * LIMS, but Intelligence not having dropdown"). The dropdowns are not something
 * ALPACA draws — they are ordinary Excel data-validation rules stored in the
 * workbook we already load. The Colilert-18 template, for example, carries three:
 *
 *     sqref="N11:N18 AF11:AG18 J3"  formula1=$AI$2:$AI$5      (Analyst)
 *     sqref="J11:J18"               formula1=$O$2:$AD$2       (Result)
 *     sqref="G11:G18"               formula1="Absent,Present" (inline list)
 *
 * We never read them, so those cells rendered as plain text with no picker.
 *
 * ALPACA ALSO adds validations at runtime that never reach the file
 * (SpreadsheetEntryController `DataValidations.Add(...)` — the True/False lists and
 * the Mold parameter range). Those are a separate port; everything authored INTO
 * the template is covered here.
 *
 * The list source is resolved to LITERAL VALUES rather than handed to Univer as a
 * range reference: the source cells sit in helper columns far to the right (AI, O:AD)
 * that the SDMS view scrolls past, they never change during a review session, and a
 * literal list cannot break if a template moves its helper block. Cross-sheet
 * references (`Qualifier!$A$1:$A$50`) resolve against the named sheet.
 */
import type ExcelJS from 'exceljs';

/** One validation rule: the ranges it covers plus the values to offer. */
export interface SheetDvRule {
  /** 0-based, end-inclusive — Univer's range shape. */
  ranges: { startRow: number; startColumn: number; endRow: number; endColumn: number }[];
  values: string[];
  /** A MULTI-select list (Univer LIST_MULTIPLE — comma-separated picks). Set for the Qualifier column
   *  (the rule sourced from the QualifierList helper sheet): the Qualifiers picker writes several
   *  symbols ("B1,BB"), which a single-select list flags "Invalid: Input must fall within specified
   *  range" (user 2026-09-24). */
  multiple?: boolean;
}

/** "AI2" / "$AI$2" → 0-based { row, col }. Returns null on anything unparseable. */
export function parseRef(ref: string): { row: number; col: number } | null {
  const m = /^\$?([A-Z]+)\$?(\d+)$/.exec(ref.trim().toUpperCase());
  if (!m) return null;
  let col = 0;
  for (const ch of m[1]!) col = col * 26 + (ch.charCodeAt(0) - 64);
  return { row: parseInt(m[2]!, 10) - 1, col: col - 1 };
}

/** Cell text exactly as a picker should show it — never "[object Object]". */
function cellText(cell: ExcelJS.Cell | undefined): string {
  if (!cell) return '';
  const v = cell.value as unknown;
  if (v == null) return '';
  if (typeof v === 'object') {
    const o = v as { result?: unknown; text?: unknown; richText?: { text?: string }[] };
    if (Array.isArray(o.richText)) return o.richText.map(r => r.text ?? '').join('');
    if (o.result !== undefined) return String(o.result ?? '');
    if (o.text !== undefined) return String(o.text ?? '');
    return '';
  }
  return String(v);
}

/**
 * Resolve a `formula1` to the values it offers.
 *
 * Two forms occur: a quoted inline list (`"Absent,Present"`) and a range reference
 * (`$O$2:$AD$2`, optionally sheet-qualified). Anything else — a named range, a
 * formula — yields an empty list, and the caller then skips the rule rather than
 * rendering an empty dropdown.
 */
export function resolveListValues(
  formula: string | undefined, wb: ExcelJS.Workbook, ws: ExcelJS.Worksheet,
): string[] {
  const f = (formula ?? '').trim();
  if (!f) return [];

  // Inline: "a,b,c" (Excel doubles an embedded quote).
  if (f.startsWith('"') && f.endsWith('"')) {
    return f.slice(1, -1).replace(/""/g, '"').split(',')
      .map(s => s.trim()).filter(s => s !== '');
  }

  // Range, optionally 'Sheet Name'!$A$1:$A$9 — strip a leading '=' first.
  const body = f.startsWith('=') ? f.slice(1) : f;
  const bang = body.lastIndexOf('!');
  let sheet = ws;
  let rangePart = body;
  if (bang >= 0) {
    const nameRaw = body.slice(0, bang).trim().replace(/^'/, '').replace(/'$/, '').replace(/''/g, "'");
    const found = wb.worksheets.find(s => s.name === nameRaw);
    if (!found) return [];
    sheet = found;
    rangePart = body.slice(bang + 1);
  }

  const [aRef, bRef] = rangePart.split(':');
  const a = parseRef(aRef ?? '');
  const b = parseRef(bRef ?? aRef ?? '');
  if (!a || !b) return [];

  const out: string[] = [];
  const seen = new Set<string>();
  for (let r = Math.min(a.row, b.row); r <= Math.max(a.row, b.row); r++) {
    for (let c = Math.min(a.col, b.col); c <= Math.max(a.col, b.col); c++) {
      const t = cellText(sheet.getRow(r + 1)?.getCell(c + 1)).trim();
      // Blank padding in the helper block is normal — skip it, and de-duplicate so a
      // template that repeats a value doesn't show it twice.
      if (t === '' || seen.has(t)) continue;
      seen.add(t);
      out.push(t);
    }
  }
  return out;
}

/** Merge single cells into horizontal runs so Univer gets a handful of ranges
 *  instead of one per cell (this template alone expands to 41). */
function toRanges(cells: { row: number; col: number }[]): SheetDvRule['ranges'] {
  const byRow = new Map<number, number[]>();
  for (const { row, col } of cells) (byRow.get(row) ?? byRow.set(row, []).get(row)!).push(col);
  const ranges: SheetDvRule['ranges'] = [];
  for (const [row, cols] of [...byRow.entries()].sort((x, y) => x[0] - y[0])) {
    cols.sort((x, y) => x - y);
    let start = cols[0]!;
    let prev = start;
    for (let i = 1; i <= cols.length; i++) {
      const c = cols[i];
      if (c !== undefined && c === prev + 1) { prev = c; continue; }
      ranges.push({ startRow: row, startColumn: start, endRow: row, endColumn: prev });
      if (c === undefined) break;
      start = c; prev = c;
    }
  }
  return ranges;
}

/**
 * Every list-type validation on one worksheet, grouped by the values it offers.
 *
 * ExcelJS expands `sqref` into a per-cell map (`ws.dataValidations.model`), which is
 * exactly what we want to read — no need to re-parse the sqref syntax — but it means
 * regrouping before handing anything to Univer.
 */
export function collectDataValidations(wb: ExcelJS.Workbook, ws: ExcelJS.Worksheet): SheetDvRule[] {
  const model = (ws as unknown as { dataValidations?: { model?: Record<string, unknown> } })
    .dataValidations?.model;
  if (!model) return [];

  // Group by the resolved value list, so N11:N18, AF11:AG18 and J3 — all pointing at
  // $AI$2:$AI$5 — become ONE rule with several ranges.
  const groups = new Map<string, { values: string[]; multiple: boolean; cells: { row: number; col: number }[] }>();
  const cache = new Map<string, string[]>();

  for (const [ref, ruleRaw] of Object.entries(model)) {
    const rule = ruleRaw as { type?: string; formulae?: unknown[] } | null;
    if (!rule || rule.type !== 'list') continue;         // only list rules become dropdowns
    const pos = parseRef(ref);
    if (!pos) continue;
    const formula = rule.formulae?.[0];
    const key = typeof formula === 'string' ? formula : '';
    let values = cache.get(key);
    if (!values) { values = resolveListValues(key, wb, ws); cache.set(key, values); }
    if (!values.length) continue;                        // nothing to offer — leave the cell plain
    const multiple = /(^|[=\s'])Qualifier(?:List)?'?!/i.test(key);   // applyQualifierDropdown's helper-sheet source ("QualifierList" before 2026-09-28)
    const gk = (multiple ? 'M\u0001' : '') + values.join('\u0000');
    const g = groups.get(gk) ?? { values, multiple, cells: [] };
    g.cells.push(pos);
    groups.set(gk, g);
  }

  return [...groups.values()].map(g => ({ ranges: toRanges(g.cells), values: g.values, ...(g.multiple ? { multiple: true } : {}) }));
}

/** A multi-select dropdown value → OG's comma-joined form. Univer writes '["B","J"]'; a value already
 *  in "B,J" form passes through. Each pick is matched case-insensitively to its option's own spelling.
 *  Null when the text is not a JSON array (nothing to rewrite). Exported for tests. */
export function qualifierPickText(text: string, options: readonly string[]): string | null {
  const t = text.trim();
  if (!t.startsWith('[')) return null;
  let picks: unknown;
  try { picks = JSON.parse(t); } catch { return null; }
  if (!Array.isArray(picks)) return null;
  const canon = (p: string) => options.find(o => o.toUpperCase() === p.toUpperCase()) ?? p;
  return picks.map(p => String(p ?? '').trim()).filter(Boolean).map(canon).join(',');
}
